import React, { useState } from "react";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
// import NotificationCenter from "../Notifications/NotificationCenter";
import "./Navbar.scss";
import DefaultLogo from "../../images/HrLabs_Logo.jpeg";
import ProfileIcon from "../ProfileIcon/ProfileIcon";
import DefaultProfileIcon from "../../images/user-1.jpg";

const Navbar = ({ onMenuToggle, notificationCount = 0 }) => {
  const { user, logout } = useAuth();

  const [menuState, setMenuState] = useState({
    quickAccessOpen: false,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  // --- DERIVED DATA from AuthContext ---
  const logoImageSrc =
    user?.logo && user?.logoType
      ? `data:${user.logoType};base64,${user.logo}`
      : DefaultLogo;
  const companyName = user?.companyName || "HRLabs";

  // FIX 1: Ensure authorities is always an array
  const authorities = Array.isArray(user?.authorities) ? user.authorities : [];
  const employeeName = user?.employeeName || "Employee";
  const profileImageSrc =
    user?.profileImage && user?.profileImageType
      ? `data:${user.profileImageType};base64,${user.profileImage}`
      : DefaultProfileIcon;

  // Format multiple roles into a readable, comma-separated string
  const formatRoleDisplay = (roleList) => {
    if (!roleList.length) return "No roles assigned";

    return roleList
      .map((authority) =>
        authority
          .replace("ROLE_", "")
          .toLowerCase()
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
      )
      .join(", ");
  };

  // --- MENU ITEMS & SEARCH ---
  const filteredMenuItems = [
    // { name: "Apply Leave", route: "/ess/apply-leave" },
    // { name: "My Leave", route: "/ess/my-leave" },
    { name: "Profile", route: "/profile" },
  ].filter((item) =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // --- EVENT HANDLERS ---
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  // const handleNotificationNavigation = (section) => {
  //   switch (section) {
  //     case "teams":
  //       navigate("/team/manage-team-leave");
  //       break;
  //     case "ess":
  //       navigate("/ess/my-logs");
  //       break;
  //     default:
  //       navigate(`/${section}`);
  //   }
  // };

  const handleLogoError = (e) => {
    console.warn("Logo failed to load, falling back to default logo");
    e.currentTarget.src = DefaultLogo;
  };

  const handleLogout = () => {
    logout();
  };

  // If the user is not logged in, don't render the navbar.
  if (!user) {
    return null;
  }

  return (
    <nav className="navbar">
      <div className="navbar-left">
        <button
          type="button"
          className="mobile-menu-button"
          aria-label="Open navigation menu"
          onClick={onMenuToggle}
        >
          <Icon icon="mdi:menu" />
        </button>
        <img
          src={logoImageSrc}
          alt="Company Logo"
          className="company-logo"
          onError={handleLogoError}
        />
        <span className="company-name">{companyName}</span>
      </div>

      <div className="navbar-center">
        <div className="search-container">
          <Icon icon="ion:search-outline" className="search-icon" />
          <input
            type="text"
            placeholder="Search assessments, tests, or skills..."
            id="search"
            className="search-input"
            value={searchQuery}
            onChange={handleSearchChange}
          />

          {searchQuery && filteredMenuItems.length > 0 && (
            <div className="filtered-menu">
              {filteredMenuItems.map((item) => (
                <Link
                  key={item.route}
                  to={item.route}
                  className="filtered-item"
                >
                  {item.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="navbar-right">
        {/* Quick Access Button */}
        <div
          className="quick-access-container"
          onMouseEnter={() =>
            setMenuState((prev) => ({ ...prev, quickAccessOpen: true }))
          }
          onMouseLeave={() =>
            setMenuState((prev) => ({ ...prev, quickAccessOpen: false }))
          }
        >
          <button className="icon-button" aria-label="Quick Access">
            <Icon icon="solar:list-arrow-down-broken" />
          </button>
          {menuState.quickAccessOpen && (
            <div className="quick-access-menu">
              {authorities.includes("ROLE_ADMIN") && (
                <>
                  <Link to="/analytics">Analytics</Link>
                  <Link to="/testmanagement">Test Management</Link>
                </>
              )}
              {authorities.includes("ROLE_USER") && (
                <>
                  <Link to="/user-history">Test History</Link>
                  <Link to="/discovermoretests">Discover More</Link>
                </>
              )}
            </div>
          )}
        </div>

        {/* Profile Button */}
        <div
          className="navbar-profile-container"
          onMouseEnter={() => setIsProfileOpen(true)}
          onMouseLeave={() => setIsProfileOpen(false)}
        >
          {isProfileOpen && (
            <div className="navbar-profile-menu">
              <div className="profile-info">
                <ProfileIcon
                  size="medium"
                  src={profileImageSrc}
                  alt="Profile"
                />
                <div className="profile-details">
                  <div className="profile-name">{employeeName}</div>
                  <div className="profile-role">
                    {formatRoleDisplay(authorities)}
                  </div>
                </div>
              </div>
              <div className="profile-options">
                <Link to="/profile">Profile</Link>
                <button
                  className="logout-button"
                  onClick={handleLogout}
                  type="button"
                >
                  Logout
                </button>
              </div>
            </div>
          )}

          <button
            className="navbar-profile-button"
            type="button"
            aria-label="Profile menu"
          >
            <ProfileIcon
              size="small"
              src={profileImageSrc}
              alt="Profile"
              className="profile-avatar"
            />
            <span className="navbar-profile-text">
              <span className="navbar-profile-name">{employeeName}</span>
              <span className="navbar-profile-role">
                {formatRoleDisplay(authorities)}
              </span>
            </span>
            <Icon icon="mdi:chevron-down" className="navbar-profile-chevron" />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
